import type { SalesResearchStatus } from '../../../api/salesResearch';
import { formatDayMonth } from '../../../utils/dateFormat';

/**
 * Pure: the line at the top of Briefs, answer first: how many briefs this
 * month, when the last one ran, what this month cost, then the all-time health.
 * "3 briefs this month · last run 13 Oct · $34.08 spent this month (USD) · 29 ready, 3 failed of 32".
 * Exported for tests.
 */
export function researchSummary(
  runs: ReadonlyArray<{ status: SalesResearchStatus; costUsd: string | number | null; createdAt?: string }>,
  now: Date = new Date(),
): string {
  const sameMonth = (iso: string | undefined) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };
  const thisMonth = runs.filter((r) => sameMonth(r.createdAt));
  const cost = thisMonth.reduce((n, r) => n + (Number(r.costUsd) || 0), 0);
  const latest = runs.reduce<string | null>((best, r) => (r.createdAt && (!best || r.createdAt > best) ? r.createdAt : best), null);
  const count = (pick: (s: SalesResearchStatus) => boolean) => runs.filter((r) => pick(r.status)).length;
  const ready = count((s) => s === 'ready');
  const failed = count((s) => s === 'failed');
  const cancelled = count((s) => s === 'cancelled');
  const going = runs.length - ready - failed - cancelled;
  return [
    `${thisMonth.length} ${thisMonth.length === 1 ? 'brief' : 'briefs'} this month`,
    latest && `last run ${formatDayMonth(latest)}`,
    `$${cost.toFixed(2)} spent this month (USD)`,
    [`${ready} ready`, failed > 0 && `${failed} failed`, cancelled > 0 && `${cancelled} cancelled`, going > 0 && `${going} running`]
      .filter(Boolean)
      .join(', ') + ` of ${runs.length} in all`,
  ]
    .filter(Boolean)
    .join(' · ');
}
